import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { bulkProcessableCount } from '@/lib/pendingProcessing';

export interface ProcessPendingBreakdown {
  /** Never screened — READY_TO_PROCESS */
  firstTime: number;
  /** Pipeline started then blocked by billing — will be retried */
  billingRetry: number;
  /** Already screened — AWAITING_DECISION / APPLIED_NEW re-run */
  reprocess?: number;
  /** Non-billing PIPELINE_ERROR retry */
  pipelineRetry?: number;
  /** PIPELINE_RUNNING — stop and re-run screening */
  restartProcessing?: number;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  breakdown: ProcessPendingBreakdown;
  busy?: boolean;
  onConfirm: () => void;
  /** Toolbar process vs bulk bar vs post-upgrade banner (open Applications list). */
  variant?: 'toolbar' | 'bulk' | 'openApplications';
  /** Plan slots left this month — shown on openApplications variant. */
  remainingCredits?: number | null;
}

function ToolbarBody({
  t,
  breakdown,
}: {
  t: (key: string, vars?: Record<string, string | number>) => string;
  breakdown: ProcessPendingBreakdown;
}) {
  const { firstTime, billingRetry } = breakdown;

  if (firstTime > 0 && billingRetry > 0) {
    return (
      <p className="text-sm text-muted-foreground">
        {t('application.processAllConfirm.bodyMixed', { firstTime, retry: billingRetry })}
      </p>
    );
  }
  if (billingRetry > 0) {
    return (
      <p className="text-sm text-muted-foreground">
        {t('application.processAllConfirm.bodyRetry', { count: billingRetry })}
      </p>
    );
  }
  return (
    <p className="text-sm text-muted-foreground">
      {t('application.processAllConfirm.bodyFirstTime', { count: firstTime })}
    </p>
  );
}

function BulkBody({
  t,
  breakdown,
}: {
  t: (key: string, vars?: Record<string, string | number>) => string;
  breakdown: ProcessPendingBreakdown;
}) {
  const {
    firstTime,
    billingRetry,
    reprocess = 0,
    pipelineRetry = 0,
    restartProcessing = 0,
  } = breakdown;

  const items: { key: string; count: number }[] = [];
  if (firstTime > 0) items.push({ key: 'application.bulkProcessConfirm.lineFirstTime', count: firstTime });
  if (restartProcessing > 0) {
    items.push({ key: 'application.bulkProcessConfirm.lineRestartProcessing', count: restartProcessing });
  }
  if (billingRetry > 0) items.push({ key: 'application.bulkProcessConfirm.lineBillingRetry', count: billingRetry });
  if (reprocess > 0) items.push({ key: 'application.bulkProcessConfirm.lineReprocess', count: reprocess });
  if (pipelineRetry > 0) items.push({ key: 'application.bulkProcessConfirm.linePipelineRetry', count: pipelineRetry });

  const showCreditsWarning = reprocess > 0 || pipelineRetry > 0 || restartProcessing > 0;

  return (
    <div className="space-y-3 text-sm text-muted-foreground">
      <p>{t('application.bulkProcessConfirm.intro')}</p>
      {items.length > 0 ? (
        <ul className="space-y-2 pl-4 list-disc marker:text-muted-foreground/70">
          {items.map(item => (
            <li key={item.key}>{t(item.key, { count: item.count })}</li>
          ))}
        </ul>
      ) : null}
      {showCreditsWarning ? (
        <p className="text-xs">{t('application.bulkProcessConfirm.creditsWarning')}</p>
      ) : null}
    </div>
  );
}

function OpenApplicationsBody({
  t,
  breakdown,
  remainingCredits,
}: {
  t: (key: string, vars?: Record<string, string | number>) => string;
  breakdown: ProcessPendingBreakdown;
  remainingCredits?: number | null;
}) {
  const total = breakdown.firstTime + breakdown.billingRetry;
  return (
    <div className="space-y-2 text-sm text-muted-foreground">
      <p>{t('application.openApplicationsConfirm.body', { count: total })}</p>
      {remainingCredits != null ? (
        <p className="text-xs font-medium text-foreground">
          {t('application.openApplicationsConfirm.credits', { count: remainingCredits })}
        </p>
      ) : null}
      <p className="text-xs">{t('application.openApplicationsConfirm.hint')}</p>
    </div>
  );
}

export default function ProcessPendingConfirmDialog({
  open,
  onOpenChange,
  breakdown,
  busy = false,
  onConfirm,
  variant = 'toolbar',
  remainingCredits = null,
}: Props) {
  const { t } = useLanguage();
  const total = bulkProcessableCount({
    firstTime: breakdown.firstTime,
    billingRetry: breakdown.billingRetry,
    reprocess: breakdown.reprocess ?? 0,
    pipelineRetry: breakdown.pipelineRetry ?? 0,
    restartProcessing: breakdown.restartProcessing ?? 0,
    blockedUpgrade: 0,
  });

  const title =
    variant === 'bulk'
      ? t('application.bulkProcessConfirm.title')
      : variant === 'openApplications'
        ? t('application.openApplicationsConfirm.title')
        : t('application.processAllConfirm.title');

  const confirmLabel =
    variant === 'bulk'
      ? t('application.bulkProcessConfirm.confirm', { count: total })
      : variant === 'openApplications'
        ? t('application.openApplicationsConfirm.confirm', { count: total })
        : t('application.processAllConfirm.confirm', { count: total });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        {variant === 'bulk' ? (
          <DialogDescription asChild>
            <BulkBody t={t} breakdown={breakdown} />
          </DialogDescription>
        ) : variant === 'openApplications' ? (
          <DialogDescription asChild>
            <OpenApplicationsBody t={t} breakdown={breakdown} remainingCredits={remainingCredits} />
          </DialogDescription>
        ) : (
          <DialogDescription asChild>
            <ToolbarBody t={t} breakdown={breakdown} />
          </DialogDescription>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
            {t('common.cancel')}
          </Button>
          <Button onClick={onConfirm} disabled={busy || total === 0}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null}
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
